import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { VerticalType } from '@rma/common-ui-domain/types';
import { appendToRmaReviewsApiUrl } from '@rma/common-ui-ng/util';
import { ProfileType } from '@rma/generic/domain/to-be-sorted';
import { RMA_DEFAULT_HTTP_OPTIONS } from '@rma/generic/util/environment';
import { httpParamMapper } from '@rma/generic/util/http';
import { map, Observable } from 'rxjs';

import type {
  ExternalReviewSource,
  ExternalReviewSourceType,
  ImportableReviewResponse,
  ImportedReviewResponse,
  ImportReviewRequest,
  LinkReviewOppsCount,
} from './domain';
import { PropertySuggestion } from './domain/property-suggestion.model';
import { PropertySuggestionRequest } from './domain/property-suggestions-request.model';

@Injectable({
  providedIn: 'root',
})
export class ExternalReviewsApiService {
  private readonly httpClient = inject(HttpClient);
  private readonly reviewsApiUrl = inject(appendToRmaReviewsApiUrl);

  public getImportExternalReviewCount(
    verticalType: VerticalType,
    profileType: ProfileType,
    profileCode: string,
  ): Observable<LinkReviewOppsCount> {
    return this.httpClient.get<LinkReviewOppsCount>(
      this.reviewsApiUrl(`${verticalType}/${profileType}/${profileCode}/Importable/Counts`),
      RMA_DEFAULT_HTTP_OPTIONS,
    );
  }

  public getOnlyImportableReviewCount(
    verticalType: VerticalType,
    profileType: ProfileType,
    profileCode: string,
    externalReviewSources: ExternalReviewSourceType[],
  ): Observable<number> {
    const params = httpParamMapper({ externalReviewSources });

    return this.httpClient.get<number>(this.reviewsApiUrl(`${verticalType}/${profileType}/${profileCode}/Importable/Count`), {
      ...RMA_DEFAULT_HTTP_OPTIONS,
      params,
    });
  }

  public getExternalReviewSources(
    verticalType: VerticalType,
    profileType: ProfileType,
    profileCode: string,
  ): Observable<ExternalReviewSource[]> {
    return this.httpClient.get<ExternalReviewSource[]>(
      this.reviewsApiUrl(`${verticalType}/${profileType}/${profileCode}/ExternalReviewSources`),
      RMA_DEFAULT_HTTP_OPTIONS,
    );
  }

  public getExternalReviews(
    verticalType: VerticalType,
    profileType: ProfileType,
    profileCode: string,
    externalReviewSources?: ExternalReviewSourceType[],
  ): Observable<ImportableReviewResponse[]> {
    const params = httpParamMapper({ externalReviewSources });

    return this.httpClient
      .get<
        ImportableReviewResponse[]
      >(this.reviewsApiUrl(`${verticalType}/${profileType}/${profileCode}/Importable`), { ...RMA_DEFAULT_HTTP_OPTIONS, params })
      .pipe(map((x) => this.decodeReviewResponse(x)));
  }

  public deleteExternalReview(
    verticalType: VerticalType,
    profileType: ProfileType,
    profileCode: string,
    externalReviewId: string,
    externalReviewSource: ExternalReviewSourceType,
  ): Observable<void> {
    const params = httpParamMapper({ externalReviewId });

    return this.httpClient.delete<void>(
      this.reviewsApiUrl(`${verticalType}/${profileType}/${profileCode}/Importable/${externalReviewSource}`),
      { params },
    );
  }

  public removePropertySuggestion(
    verticalType: VerticalType,
    profileType: ProfileType,
    profileCode: string,
    externalReviewId: string,
    externalReviewSource: ExternalReviewSourceType,
    campaignId: string,
  ): Observable<void> {
    const params = httpParamMapper({ externalReviewId, campaignId });

    return this.httpClient.delete<void>(
      this.reviewsApiUrl(`${verticalType}/${profileType}/${profileCode}/Importable/PropertySuggestions/${externalReviewSource}`),
      { params },
    );
  }

  public getPropertySuggestions(
    verticalType: VerticalType,
    profileType: ProfileType,
    profileCode: string,
    propertySuggestionRequest: PropertySuggestionRequest,
  ): Observable<PropertySuggestion[]> {
    const params = httpParamMapper(propertySuggestionRequest);

    return this.httpClient.get<PropertySuggestion[]>(
      this.reviewsApiUrl(`${verticalType}/${profileType}/${profileCode}/Importable/PropertySuggestions`),
      { ...RMA_DEFAULT_HTTP_OPTIONS, params },
    );
  }

  public getExternalReview(
    verticalType: VerticalType,
    profileType: ProfileType,
    profileCode: string,
    externalReviewId: string,
    externalReviewSource: ExternalReviewSourceType,
  ): Observable<ImportableReviewResponse> {
    const params = httpParamMapper({ externalReviewId });

    return this.httpClient.get<ImportableReviewResponse>(
      this.reviewsApiUrl(`${verticalType}/${profileType}/${profileCode}/Importable/${externalReviewSource}`),
      { ...RMA_DEFAULT_HTTP_OPTIONS, params },
    );
  }

  public getExternalReviewCountForAgent(providerType: ExternalReviewSourceType, externalAgentId: string): Observable<number> {
    return this.httpClient
      .get<{
        [param: string]: number;
      }>(this.reviewsApiUrl(`/ExternalReviews/${providerType}/ReviewCounts?externalAgentIds=${externalAgentId}`), RMA_DEFAULT_HTTP_OPTIONS)
      .pipe(map((x) => x[externalAgentId.toLocaleLowerCase()]));
  }

  public importExternalReview(
    verticalType: VerticalType,
    profileType: ProfileType,
    profileCode: string,
    request: ImportReviewRequest,
  ): Observable<ImportedReviewResponse> {
    return this.httpClient.post<ImportedReviewResponse>(
      this.reviewsApiUrl(`${verticalType}/${profileType}/${profileCode}/import`),
      request,
      RMA_DEFAULT_HTTP_OPTIONS,
    );
  }

  public getHasImportableReviews(verticalType: VerticalType, profileType: ProfileType, profileCode: string): Observable<boolean> {
    return this.httpClient.get<boolean>(
      this.reviewsApiUrl(`${verticalType}/${profileType}/${profileCode}/Importable/HasAny`),
      RMA_DEFAULT_HTTP_OPTIONS,
    );
  }

  private decodeHtmlEntities(raw: string): string {
    const txt = document.createElement('textarea');
    txt.innerHTML = raw;
    return txt.value;
  }

  // TODO: remove this once we have the reviews cleaned up in the DB
  private decodeReviewResponse(importableReviewResponses: ImportableReviewResponse[]): ImportableReviewResponse[] {
    return importableReviewResponses.map((importableReviewResponse) => this.decodeReview(importableReviewResponse));
  }

  private decodeReview(importableReviewResponse: ImportableReviewResponse): ImportableReviewResponse {
    return {
      ...importableReviewResponse,
      importableReview: {
        ...importableReviewResponse.importableReview,
        review: this.decodeHtmlEntities(importableReviewResponse.importableReview.review),
        reviewerName: this.decodeHtmlEntities(importableReviewResponse.importableReview.reviewerName),
      },
    };
  }
}
